import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getChannelList,
  getChannelById,
  getChannelTypeList,
  getChannelTypeFieldsList,
  createChannel,
  updateChannel,
  deleteChannel,
} from '@/services/rulesService.js';
import { QUERY_KEYS } from '@/shared/constant/queryKeys.js';
import { CACHE_TIMES } from '@/shared/config/reactQuery';

const useChannel = (
  name = '',
  isActive = null,
  page = 1,
  pageSize = 10,
  channelId = null,
  channelTypeId = null,
  enableChannelTypes = false,
  enableChannelList = true,
) => {
  const queryClient = useQueryClient();

  const {
    data: channelList,
    isLoading: isLoadingChannels,
    isFetching: isFetchingChannels,
    error: channelsError,
    refetch: refetchChannels,
  } = useQuery({
    queryKey: QUERY_KEYS.CHANNEL_LIST(
      name,
      isActive,
      channelTypeId,
      page,
      pageSize,
    ),
    queryFn: () =>
      getChannelList(name, isActive, channelTypeId, page, pageSize),
    select: (response) => ({
      items: response.data?.channels || [],
      total: response.meta?.total || 0,
      page: response.meta?.page || page,
      pageSize: response.meta?.size || pageSize,
      pages: response.meta?.pages || 1,
      counts: {
        totalCount: response.data?.total_count || 0,
        activeCount: response.data?.active_count || 0,
        inactiveCount: response.data?.inactive_count || 0,
      },
    }),
    enabled: enableChannelList,
    staleTime: CACHE_TIMES.MEDIUM,
    gcTime: CACHE_TIMES.LONG,
  });

  const {
    data: channelDetail,
    isLoading: isLoadingChannelDetail,
    error: channelDetailError,
    refetch: refetchChannelDetail,
  } = useQuery({
    queryKey: QUERY_KEYS.CHANNEL_DETAIL(channelId),
    queryFn: () => getChannelById(channelId),
    enabled: !!channelId,
    staleTime: CACHE_TIMES.MEDIUM,
    gcTime: CACHE_TIMES.LONG,
  });

  const {
    data: channelTypeList,
    isLoading: isLoadingChannelTypes,
    error: channelTypesError,
    refetch: refetchChannelTypes,
  } = useQuery({
    queryKey: QUERY_KEYS.CHANNEL_TYPE_LIST,
    queryFn: () => getChannelTypeList(),
    enabled: enableChannelTypes,
    staleTime: CACHE_TIMES.VERY_LONG,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const {
    data: channelTypeFields,
    isLoading: isLoadingChannelTypeFields,
    error: channelTypeFieldsError,
    refetch: refetchChannelTypeFields,
  } = useQuery({
    queryKey: QUERY_KEYS.CHANNEL_TYPE_FIELDS(channelTypeId),
    queryFn: () => getChannelTypeFieldsList(channelTypeId),
    enabled: !!channelTypeId,
    staleTime: CACHE_TIMES.VERY_LONG,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const {
    mutate: createChannelMutation,
    mutateAsync: createChannelAsync,
    isPending: isCreatingChannel,
    error: createChannelError,
  } = useMutation({
    mutationFn: ({ name, channel_type_id, extra_config }) =>
      createChannel(name, channel_type_id, extra_config),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.CHANNEL_ALL,
      });
    },
  });

  const {
    mutate: updateChannelMutation,
    mutateAsync: updateChannelAsync,
    isPending: isUpdatingChannel,
    error: updateChannelError,
  } = useMutation({
    mutationFn: ({
      channelId,
      name,
      channel_type_id,
      is_active,
      extra_config,
    }) =>
      updateChannel(channelId, name, channel_type_id, is_active, extra_config),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.CHANNEL_ALL,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.CHANNEL_DETAIL(variables.channelId),
      });
    },
  });

  const {
    mutate: deleteChannelMutation,
    mutateAsync: deleteChannelAsync,
    isPending: isDeletingChannel,
    error: deleteChannelError,
  } = useMutation({
    mutationFn: (channelId) => deleteChannel(channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.CHANNEL_ALL,
      });
    },
  });

  return {
    channelList,
    isLoadingChannels,
    isFetchingChannels,
    channelsError,
    refetchChannels,

    channelDetail,
    isLoadingChannelDetail,
    channelDetailError,
    refetchChannelDetail,

    channelTypeList,
    isLoadingChannelTypes,
    channelTypesError,
    refetchChannelTypes,

    channelTypeFields,
    isLoadingChannelTypeFields,
    channelTypeFieldsError,
    refetchChannelTypeFields,

    createChannel: createChannelMutation,
    createChannelAsync,
    isCreatingChannel,
    createChannelError,

    updateChannel: updateChannelMutation,
    updateChannelAsync,
    isUpdatingChannel,
    updateChannelError,

    deleteChannel: deleteChannelMutation,
    deleteChannelAsync,
    isDeletingChannel,
    deleteChannelError,
  };
};

export default useChannel;
